import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQuery, UseQueryResult } from "react-query";
import { useEntitiesList } from "src/search/lib/useEntitiesList";
import { useInputParams } from "src/search/lib/useInputParams";
import { useTimeSeries } from "src/search/lib/useTimeSeries";
import { SearchState } from "src/search/types";

export function useEntity({
  orgId,
  entityUuid,
  entityList,
  entityIndex,
  useHistory,
}: Omit<
  EntityContext,
  | "query"
  | "forward"
  | "back"
  | "hasBack"
  | "hasForward"
  | "nextUuid"
  | "previousUuid"
  | "next"
  | "previous"
  | "setUuid"
  | "dataRef"
>): EntityContext {
  const [uuid, setUuid] = useState<string>(entityUuid);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(0);
  const [hasBack, setHasBack] = useState<boolean>(false);
  const [hasForward, setHasForward] = useState<boolean>(false);
  const [nextUuid, setNextUuid] = useState<string | undefined>(undefined);
  const [previousUuid, setPreviousUuid] = useState<string | undefined>(
    undefined
  );
  const uniqueList = [...new Set(entityList || [])];
  const [list] = useState<string[]>(uniqueList);
  useEffect(() => {
    if (!history.includes(uuid)) {
      setHistory([...history.slice(0, historyIndex + 1), uuid]);
      setHistoryIndex(history.length);
    }
    setHasBack(historyIndex > 0 && history.length > 0);
    setHasForward(historyIndex < history.length - 1);
    const index = list.indexOf(uuid);
    setNextUuid(
      index !== undefined && index >= 0 && index < list.length
        ? list[index + 1]
        : undefined
    );
    setPreviousUuid(
      index !== undefined && index > 0 && list ? list[index - 1] : undefined
    );
  }, [historyIndex, history, uuid, list]);

  const forward = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setUuid(history[historyIndex + 1]);
    }
  };
  const back = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setUuid(history[historyIndex - 1]);
    }
  };
  const query = useQuery(
    ["entity", { orgId, uuid }],
    async () => {
      return fetch(`/api/${orgId}/entity-detail`, {
        method: "POST",
        body: JSON.stringify({
          orgId,
          entityUuid: uuid,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      }).then((res) => res.json());
    },
    {
      keepPreviousData: true,
      staleTime: 20_000,
    }
  );
  const dataRef = useRef(query.data);
  dataRef.current = query.data || dataRef.current;
  return {
    orgId,
    entityUuid,
    entityIndex,
    entityList: list,
    useHistory,
    hasBack,
    hasForward,
    forward,
    back,
    nextUuid,
    previousUuid,
    setUuid,
    dataRef,
    query,
  };
}

export type EntityContext = {
  orgId: string;
  entityUuid: string;
  entityList?: string[];
  entityIndex?: number;
  useHistory?: boolean;
  hasBack: boolean;
  hasForward: boolean;
  forward: () => void;
  back: () => void;
  nextUuid: string | undefined;
  previousUuid: string | undefined;
  dataRef: React.MutableRefObject<any>;
  query: UseQueryResult<any>;
  setUuid: React.Dispatch<React.SetStateAction<string>>;
};

export const entityContext = createContext<EntityContext>(
  undefined as unknown as EntityContext
);

export function useEntityContext() {
  return useContext(entityContext);
}

export type EntitySearchTimeSeries = {
  params: ReturnType<typeof useInputParams>;
  timeSeries: ReturnType<typeof useTimeSeries>;
};

export type EntitySearchContext = {
  params: ReturnType<typeof useInputParams>;
  entitiesList: ReturnType<typeof useEntitiesList>;
};

export function useEntityTimeSeries(
  initial: SearchState
): EntitySearchTimeSeries {
  const params = useInputParams({ ...initial });
  const timeSeries = useTimeSeries(params);

  return {
    params,
    timeSeries,
  };
}

export function useEntitySearch(initial: SearchState): EntitySearchContext {
  const params = useInputParams({ ...initial });
  const entitiesList = useEntitiesList(params);

  return {
    params,
    entitiesList,
  };
}
